// Styles
import './ApplicationSelect.scss';

import { MenuProps, components } from 'react-select';
import React, { useState } from 'react';

import { ApplicationSearchQuery } from '~/graphqlTypes/ApplicationSearchQuery';
import AsyncSelect from 'react-select/async';
import { SingleApplicationSearchQuery } from '~/graphqlTypes/SingleApplicationSearchQuery';
import applicationSearchQuery from './ApplicationSearchQuery.gql';
import singleApplicationSearchQuery from './SingleApplicationSearchQuery.gql';
import { useDebouncedCallback } from 'use-debounce';
import { useLazyQuery } from '@apollo/client';
import { ExplicitnessRating } from '~/graphqlTypes/globalTypes';

export interface ApplicationSelectProps {
  className?: string;
  onChange?: ( app: SelectedApp | null, selectedInstanceSet?: boolean ) => void;

  /** When true (the default), don't search for or render apps that aren't part of an instance set */
  excludeLegacyApps?: boolean;

  /** When true (the default), blur the input when a selection is made */
  blurInputOnSelect?: boolean;

  value?: SelectedApp | null;
}

export interface SelectedApp {
  id: number;
  name: string;

  /** When true, the user selected an instance set. Otherwise, the user select a specific prod/dev/legacy app */
  selectedInstanceSet: boolean;

  /** When a returned app is part of an instance set, this will be set. */
  instanceSet?: {
    id: number;
    name: string;

    crossfadeSeconds: number | null;
    preGain: number | null;
    rating: ExplicitnessRating | null;

    productionId: number;
    productionName: string;

    developmentId: number;
    developmentName: string;
  };
}

type SelectOption = SelectedApp;

interface GroupSelectOptions {
  label: string;
  options: Array<SelectedApp>;
}

function hasOption( options: readonly ( SelectOption | GroupSelectOptions )[] ): boolean {
  const option = options.find( ( curr ) => {
    if ( 'options' in curr ) return hasOption( curr.options );
    return 'id' in curr;
  } );

  return Boolean( option );
}

const Menu = ( props: MenuProps<SelectOption, false, GroupSelectOptions> ) => {
  if ( !hasOption( props.options ) && !props.isLoading ) {
    return <></>;
  } else {
    return (
      <>
        <components.Menu<SelectOption, false, GroupSelectOptions> { ...props }>{ props.children }</components.Menu>
      </>
    );
  }
};

const Nothing = () => {
  return <></>;
};

export const ApplicationSelect = ( props: ApplicationSelectProps ) => {
  const { onChange, excludeLegacyApps = true, value, blurInputOnSelect } = props;

  const [applicationSearch, { loading: searchLoading }] = useLazyQuery<ApplicationSearchQuery>( applicationSearchQuery );
  const [singleApplicationSearch, { loading: singleLoading }] =
    useLazyQuery<SingleApplicationSearchQuery>( singleApplicationSearchQuery );
  const [selectedValue, setSelectedValue] = useState<SelectOption | null>( value ?? null );

  const handleChange = ( newValue: SelectOption | null /*, actionMeta: ActionMeta */ ) => {
    if ( typeof value === 'undefined' ) {
      // enclosing component is not managing the value, so we need to update the internal state
      setSelectedValue( newValue );
    }

    if ( onChange && newValue !== null ) {
      onChange( newValue, newValue.selectedInstanceSet );
    } else if ( onChange /* newValue === null */ ) {
      onChange( null );
    }
  };

  const formatOptionLabel = ( option: SelectOption ) => {
    return (
      <>
        { option.name }{ ' ' }
        <span className="details">
          { option.instanceSet
            ? `${option.instanceSet.developmentId} and ${option.instanceSet.productionId}`
            : option.id }
        </span>
      </>
    );
  };

  const getOptionLabel = ( option: SelectOption ) => {
    return option.name;
  };

  const getOptionValue = ( option: SelectOption ) => {
    return option.id.toString();
  };

  const loadOptions = useDebouncedCallback(
    ( inputValue: string, callback: ( options: Array<SelectOption | GroupSelectOptions> ) => void ) => {
      if ( inputValue.match( /^\w*\d+\w*$/ ) ) {
        // user is entering an app id
        singleApplicationSearch( { variables: { appId: inputValue } } ).then( ( { data } ) => {
          if ( !data ) {
            return [];
          }

          const app = data.application;

          if ( app.instances ) {
            // single result with an instance set
            callback( [
              {
                id: app.id,
                name: app.name,

                // an instance set is associated with the app, but the user punched in a specific app id
                selectedInstanceSet: false,

                instanceSet: {
                  id: app.instances.id,
                  name: app.name ?? `${app.instances.production.name} and ${app.instances.development.name}`,

                  crossfadeSeconds: app.instances.crossfadeSeconds,
                  preGain: app.instances.preGain,
                  rating: app.instances.rating,

                  developmentId: app.instances.development.id,
                  developmentName: app.instances.development.name,

                  productionId: app.instances.production.id,
                  productionName: app.instances.production.name,
                },
              },
            ] );
          } else {
            // single legacy app
            callback( [
              {
                id: app.id,
                name: app.name,

                selectedInstanceSet: false,
              },
            ] );
          }
        } );
      } else {
        // user is doing string search
        applicationSearch( { variables: { name: inputValue } } ).then( ( { data } ) => {
          if ( !data ) {
            return [];
          }

          const isInstanceSet = data.applicationInstanceSets.data.reduce( ( accum, app ) => {
            accum.add( app.development.id );
            accum.add( app.production.id );
            return accum;
          }, new Set<number>() );

          const options: Array<GroupSelectOptions> = [
            {
              // first list out apps that are part of instance sets (1 entry per set)
              label: 'Apps',
              options: data.applicationInstanceSets.data.map( ( app ) => {
                return {
                  // for legacy reasons, we return the development app id at the top level
                  id: app.development.id,
                  name: app.name ?? `${app.production.name} and ${app.development.name}`,

                  selectedInstanceSet: true,

                  instanceSet: {
                    id: app.id,
                    name: app.name ?? `${app.production.name} and ${app.development.name}`,

                    crossfadeSeconds: app.crossfadeSeconds,
                    preGain: app.preGain,
                    rating: app.rating,

                    developmentId: app.development.id,
                    developmentName: app.development.name,
                    productionId: app.production.id,
                    productionName: app.production.name,
                  },
                };
              } ),
            },
          ];

          if ( !excludeLegacyApps ) {
            options.push( {
              // then list out 'regular' apps
              label: 'Legacy Apps',
              options: data.applicationSearch.results
                .filter( ( result ) => !isInstanceSet.has( result.application.id ) )
                .map( ( result ) => {
                  const app = result.application;

                  return {
                    id: app.id,
                    name: app.name,

                    selectedInstanceSet: false,
                  };
                } ),
            } );
          }

          callback( options );
        } );
      }
    },
    500,
  );

  return (
    <AsyncSelect<SelectOption, false, GroupSelectOptions>
      data-testid="ops-application-select"
      classNamePrefix="ops-application-select"
      cacheOptions
      blurInputOnSelect={ blurInputOnSelect ?? true }
      loadingMessage={ () => 'Hang tight.. searching...' }
      placeholder="Type app name or id to search..."
      isLoading={ searchLoading || singleLoading }
      isOptionSelected={ ( option, val ) =>
        val && val[0] && option.id === val[0].id && option.selectedInstanceSet === val[0].selectedInstanceSet
      }
      onChange={ handleChange }
      loadOptions={ loadOptions }
      formatOptionLabel={ formatOptionLabel }
      getOptionLabel={ getOptionLabel }
      getOptionValue={ getOptionValue }
      components={ { Menu, DropdownIndicator: Nothing } }
      value={ typeof value === 'undefined' ? selectedValue : value }
    />
  );
};

ApplicationSelect.displayName = 'ApplicationSelect';

/*
  Specify permissions required to access the component.
  Must be paired with <RestrictedAccess /> to work properly.
*/
ApplicationSelect.requiredPermissions = [] as string[];
